import { Injectable, OnDestroy } from '@angular/core';
import { Feature, Role } from '@ktypes/enums';
import {
  AuthData,
  DataStatus,
  Group,
  LiveSupport,
  Organization,
  ScreenDashboard,
  Settings,
  Status,
  StatusMessage,
  Theme,
  User,
} from '@ktypes/models';
import { clearEmptyProps } from '@kutil';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import { BehaviorSubject, Observable, distinctUntilChanged, skipWhile, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { BrowserStorage } from './browser-storage.service';
import { ThemeService } from './theme.service';

export interface Store {
  authData?: AuthData;
  pulseSurveyAuthData?: AuthData;
  user?: User;
}

@Injectable({
  providedIn: 'root',
})
export class DataStoreService implements OnDestroy {
  /* NOTE: The Data Store should only be accessed via BLoC files, not directly by components. */
  constructor(private _browserStorage: BrowserStorage, private _themeService: ThemeService) {
    this._loadAuthData();
  }

  private _store: Store = {};
  private _store$ = new BehaviorSubject<Store>({});
  private _latestAuthStatus$ = new BehaviorSubject<DataStatus<AuthData>>(null);
  private _dashboard = new BehaviorSubject<DataStatus<ScreenDashboard>>(
    new DataStatus<ScreenDashboard>(
      Status.starting,
      new StatusMessage('STARTING', 'Initial data'),
      new ScreenDashboard()
    )
  );
  private _settingChangeInProgress = false;

  store$: Observable<Store> = this._store$.pipe(distinctUntilChanged());
  authData$: Observable<AuthData> = this.store$.pipe(
    distinctUntilChanged(),
    map((store) => store.pulseSurveyAuthData ?? store.authData)
  );
  user$: Observable<User> = this.store$.pipe(
    distinctUntilChanged(),
    map((store) => store.user)
  );
  featureKeys$: Observable<Feature[]> = this.store$.pipe(
    distinctUntilChanged(),
    map((store) => store.user?.features?.map((feature) => feature?.key))
  );
  roleKeys$: Observable<Role[]> = this.store$.pipe(
    distinctUntilChanged(),
    map((store) => store.user?.roles?.map((role) => role?.key))
  );

  ngOnDestroy(): void {
    this._store$.complete();
  }

  // Get access to the stream of authentication events
  get authStatus$(): Observable<DataStatus<AuthData>> {
    return this._latestAuthStatus$.asObservable();
  }

  get authStatus(): DataStatus<AuthData> {
    return this._latestAuthStatus$.getValue();
  }

  setAuthStatus(dataStatus: DataStatus<AuthData>) {
    this._latestAuthStatus$.next(dataStatus);
  }

  resetStatus(starting = false) {
    if (starting) {
      this.setAuthStatus(
        new DataStatus<AuthData>(Status.starting, new StatusMessage(Status.starting, ''), this._store.authData)
      );
    } else {
      this.setAuthStatus(null);
    }
  }

  /// NOTE: This will only get the current value of the store, it does not stream the changes
  get store(): Store {
    // return a clone of the store, so the underlying data cannot be
    // manipulated outside the methods available in this service
    return cloneDeep(this._store);
  }

  get authData(): AuthData {
    return cloneDeep(this._store.pulseSurveyAuthData ?? this._store.authData);
  }

  setAuthData(authData: any) {
    // TODO: This also updates user, we should separate the two and store token data and user data separately
    if (
      !authData ||
      isEqual(new AuthData().deserialize({ ...authData }), new AuthData().deserialize({ ...this._store.authData }))
    ) {
      return;
    }
    if (authData?.token != null && typeof authData.token !== 'string') {
      // has token, but it's an invalid token type (not a string), don't store/process it
      console.warn('has token, but is an invalid token type (not a string) - aborting processing in setAuthData');
      return;
    }
    const user = new User().deserialize(merge({}, this._store.authData?.user, authData.user));
    if (user?.group && !isEqual(this._store.authData?.user?.group?.liveSupport, user?.group?.liveSupport)) {
      const liveSupport = merge(
        clearEmptyProps(this._store.user?.group?.liveSupport),
        clearEmptyProps(user?.group?.liveSupport)
      );
      user.group.liveSupport = new LiveSupport().deserialize(liveSupport);
      authData.user = user;
    }
    this._store = merge(
      {},
      this._store,
      { authData: new AuthData().deserialize(merge({}, this._store.authData, authData)) },
      {
        pulseSurveyAuthData: this._store.pulseSurveyAuthData
          ? new AuthData().deserialize(merge({}, this._store.pulseSurveyAuthData))
          : null,
      },
      {
        user: new User()
          .deserialize(merge({}, this._store.authData?.user))
          .combine(this._store.user)
          .combine(authData.user),
      }
    );
    this._store$.next(this._store);
    this.saveToStorage();
  }

  removeAuthData() {
    this._store = merge({}, this._store);
    if (this._store.authData) {
      delete this._store.authData;
      this._browserStorage.remove('authData', true);
    }
    if (this._store.user) {
      delete this._store.user;
      this._browserStorage.remove('user', true);
    }
    this._store$.next(this._store);
  }

  setPulseSurveyAuthData(pulseSurveyAuthData: any) {
    if (
      isEqual(
        new AuthData().deserialize({ ...pulseSurveyAuthData }),
        new AuthData().deserialize({ ...this._store.pulseSurveyAuthData })
      )
    ) {
      return;
    }
    if (!pulseSurveyAuthData) {
      delete this._store.pulseSurveyAuthData;
      const authData = new AuthData().deserialize(
        merge({}, this._store.authData, {
          user: new User().deserialize(merge({}, this._store.authData?.user, this._store.user)),
        })
      );
      this._store = merge(
        {},
        this._store,
        { authData },
        { user: new User().deserialize(merge({}, authData.user, this._store.user)) }
      );
    } else {
      this._store = merge({}, this._store, {
        pulseSurveyAuthData: new AuthData().deserialize(
          merge({}, this._store.pulseSurveyAuthData ? this._store.pulseSurveyAuthData : {}, pulseSurveyAuthData)
        ),
      });
    }
    this._store$.next(this._store);
    this.saveToStorage();
  }

  get user(): User {
    return cloneDeep(this._store.user);
  }

  // updateStreamAndSave is only for facilitating usage with extractDashboardData
  // TODO: remove once no longer used for dashboard data
  setUser(data: any, updateStreamAndSave = true) {
    if (
      !data ||
      isEqual(
        new User().deserialize({ ...data }),
        new User().deserialize({ ...(this._store.user || this._store.authData?.user || {}) })
      )
    ) {
      return;
    }
    const user = new User().deserialize(merge({}, this._store.authData?.user, data));
    if (user.group && !isEqual(this._store.user.group?.liveSupport, user.group?.liveSupport)) {
      const liveSupport = merge(
        clearEmptyProps(this._store.user.group?.liveSupport),
        clearEmptyProps(user.group?.liveSupport)
      );
      user.group.liveSupport = new LiveSupport().deserialize(liveSupport);
    }
    if ((data.group?.theme || data.theme) && !user.settings?.darkMode) {
      const theme = new Theme().deserialize(data.group?.theme || data.theme);
      this._themeService.changeTheme(theme);
    }
    if (updateStreamAndSave) {
      this._store = merge(
        {},
        this._store,
        { user },
        {
          authData: new AuthData().deserialize(
            merge({}, { ...this.store.authData }, { ...{ userId: user?.id, user } })
          ),
        }
      );
      this._store$.next(this._store);
      this.saveToStorage();
    }
    // Once this is removed, look at moving Dashboard related functionality back to Insightful
    this.extractDashboardData(data);
  }

  removeUser() {
    this._store = merge({}, this._store);
    if (this._store.authData?.user) {
      delete this._store.authData.user;
    }
    if (this._store.user) {
      delete this._store.user;
      this._browserStorage.remove('user', true);
    }
    this._store$.next(this._store);
  }

  // TODO: remove once no longer used for dashboard data
  get dashboard$(): Observable<DataStatus<ScreenDashboard>> {
    return this._dashboard.asObservable();
  }

  // TODO: remove once no longer used for dashboard data
  get dashboard(): DataStatus<ScreenDashboard> {
    return this._dashboard.value;
  }

  // TODO: remove once no longer used for dashboard data
  setDashboard(dashboardData: any, status: Status) {
    const loadingStatus = new StatusMessage('LOADING', 'Loading...');
    const doneStatus = new StatusMessage('DONE', 'Loaded');
    this._dashboard.next(
      new DataStatus<ScreenDashboard>(
        status,
        status !== Status.starting ? doneStatus : loadingStatus,
        new ScreenDashboard().deserialize(dashboardData, this.dashboard.data)
      )
    );
  }

  // TODO: remove once no longer used for dashboard data
  extractDashboardData(data: User | Organization[] | Group[]) {
    const dashboardWidgets = ['error'];
    const normalizedData = Array.isArray(data) ? data : [data];
    const widgetData = normalizedData?.[0] || {};

    if (
      this._dashboard.value?.status === Status.starting &&
      (normalizedData?.length !== 1 ||
        !dashboardWidgets.some(
          (widgetName) => Object.keys(widgetData).includes(widgetName) && widgetData[widgetName]?.length > 0
        ))
    ) {
      // if not a single item, or doesn't have any data to be
      // included on the dashboard, don't continue processing
      return;
    }

    const dashboardData = dashboardWidgets.reduce<ScreenDashboard>(
      (dashboardData: ScreenDashboard, widgetName: string) => {
        if (widgetData[widgetName] != null) {
          dashboardData[widgetName] = widgetData[widgetName];
        }
        return dashboardData;
      },
      {} as ScreenDashboard
    );

    if (Object.keys(dashboardData).length > 0) {
      this.setDashboard(dashboardData, Object.keys(dashboardData).includes('error') ? Status.error : Status.done);
    } else {
      this.setDashboard(null, Status.error);
    }
  }

  saveToStorage() {
    const lifespan = this.stayLoggedIn ? 30 : new Date(new Date().getTime() + 60 * 60 * 1000);
    this._browserStorage.setObject('authData', this._store.authData, lifespan, true);
    this._browserStorage.setObject('user', this._store.user, lifespan, true);
  }

  updateOnFocus() {
    const storedAuthData: AuthData = this._browserStorage.getObject('authData', true) ?? {};
    const storedUser = (this._browserStorage.getObject('user', true) ?? {}) as User;
    if (!storedAuthData || !storedUser) {
      // don't update if there is not any storedAuthData/storedUser (i.e. after deleted by logout)
      return;
    }
    if (
      storedAuthData?.dateExpires !== this.authData?.dateExpires ||
      storedAuthData?.token !== this.authData?.token ||
      storedUser?.id !== this.user?.id ||
      storedUser?.type !== this.user?.type
    ) {
      this.setAuthData({ ...storedAuthData, user: { ...storedUser }, userId: storedUser.id });
    }
  }

  updateOnStorageChange(event: StorageEvent) {
    const keysToWatch = [];
    if (Object.keys(this.store)?.includes(event?.key) && keysToWatch.includes(event?.key)) {
      // do stuff on storage change
    }
  }

  private _loadAuthData(): void {
    const loadedAuthData = this._browserStorage.getObject('authData', true);
    const loadedUser = this._browserStorage.getObject('user', true);
    const user = new User().deserialize(
      merge({}, this._store.authData?.user, this.user, loadedAuthData?.user, loadedUser)
    );
    const authData = new AuthData().deserialize(merge({}, this.authData, { user }, loadedAuthData));
    this.setAuthData(authData);
    this.setUser(user);
  }

  setUserSettings(settings: Settings) {
    if (settings) {
      settings.staySignedIn = this.stayLoggedIn;
    }
    const authData = new AuthData().deserialize({
      ...this._store.authData,
      user: {
        ...this._store.authData.user,
        settings: {
          ...this._store.authData.user.settings,
          ...settings,
        },
      },
    });
    if (this._store.pulseSurveyAuthData) {
      this.setPulseSurveyAuthData(authData);
    }
    this.setAuthData(authData);
    this.setUser(authData.user);
    this._themeService.forceHighContrast(settings?.darkMode);
  }

  setLoggedIn(stayLoggedIn: boolean) {
    this._browserStorage.setObject('stayLoggedIn', stayLoggedIn, 30, true);
    if (!this._settingChangeInProgress) {
      this._settingChangeInProgress = true;
      // This subscription is needed for setting stayLoggedIn on ScreenLogin
      this.authData$
        .pipe(
          skipWhile((authData) => authData == null || !authData?.token || !authData?.user?.id),
          take(1)
        )
        .subscribe((authData) => {
          this.setUserSettings(
            new Settings().deserialize({
              ...(authData.user?.settings || {}),
              staySignedIn: this._browserStorage.getObject('stayLoggedIn', true),
            })
          );
          this._settingChangeInProgress = false;
        });
    }
  }

  get stayLoggedIn(): boolean {
    return this._browserStorage.getObject('stayLoggedIn', true);
  }
}
